/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
/* eslint-disable ember/no-runloop */
import type ApplicationInstance from "@ember/application/instance";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { next } from "@ember/runloop";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { modifier } from "ember-modifier";
import type JournalEventModel from "parog-web/models/journal-event";
import type JesHistoryTableComponent from "..";

interface Args {
  table: JesHistoryTableComponent;
  propertyName: string;
  feature: string;
  component: string;
  onInit: (context: JesHistoryTableColumnComponent) => void;
  jeClass: { new (): JournalEventModel };
  disableTimePopup: boolean;
  modelName: string;
}

interface ColumnValue {
  element: HTMLElement;
  value?: JournalEventModel;
  je?: JournalEventModel;
}

export default class JesHistoryTableColumnComponent extends Component<Args> {
  @tracked labelElement;
  @tracked values: ColumnValue[] = [];
  @tracked edits: ColumnValue[] = [];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.args.onInit(this);
  }

  get patient() {
    return this.args.table.patient;
  }

  get propertyName() {
    return this.args.propertyName;
  }

  get fullComponentId() {
    return Boolean(this.args.feature) && Boolean(this.args.component)
      ? `${this.args.feature}/${this.args.component}`
      : "";
  }

  get jeClass() {
    return (
      this.args.jeClass ||
      (getOwner(this) as ApplicationInstance).factoryFor(
        `model:${this.args.modelName}`
      )?.class
    );
  }

  get patientRelationName() {
    // Transform `weight-measured-journal-event` to `weightMeasuredJournalEvents`
    return `${this.args.modelName.replace(/-./g, m => m[1].toUpperCase())}s`;
  }

  get disableTimePopup() {
    return this.args.disableTimePopup;
  }

  @action
  _addValue(element, [value]) {
    next(() => {
      this.values.push({ element, value });
      // eslint-disable-next-line no-self-assign
      this.values = this.values; // self assign to keep reactivity
    });
  }

  addValue = modifier(this._addValue);

  @action
  _addEdit(element, [je]) {
    next(() => {
      this.edits.push({ element, je });
      // eslint-disable-next-line no-self-assign
      this.edits = this.edits; // self assign to keep reactivity
    });
  }

  addEdit = modifier(this._addEdit);

  setLabelElement = modifier(this._setLabelElement);

  @action
  _setLabelElement(el) {
    this.labelElement = el;
  }
}
